import { parseISO } from 'date-fns';
import { Anchor } from 'src/models/cart/_types';
import { hasIntervalLimit } from 'src/models/user-package/utils';
import { camelcaseKeys } from 'src/utils/camelize-keys';
import { formatISODate, timestampToDate } from 'src/utils/dateFormatter';
import { decamelizeKeys } from 'src/utils/decamelize-keys';
import { removeReadOnlyFields } from 'src/utils/fieldNormalizers';

import { Package, PackageSearchResult, ServiceCount } from './_types';

const readOnlyFields = [
  'tags',
  'image_url',
  'package_limit_booking',
  'required_assessment_id',
  'location_name',
  'assigned_trainer_archived',
  'assigned_trainer_name',
];

// Fields to strip from service_counts before sending to the API.
// "ReadOnly" means React never writes these back — either they belong to the
// legacy Ember app or they are purely UI-side state with no backend counterpart.
const serviceCountReadOnlyFields = [
  'cadence', // managed by legacy Ember; React does not expose this field, must not overwrite it
  'count1',  // UI-only virtual field driving the "unlimited" toggle; has no backend counterpart
  '_limit_booking',
  '_limitBooking',
  '_unlimited',
];

const toArray = (value: any) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];

  return [value];
};

const normalizeAlternatePricesForForm = (alternatePrices: any[] = []) => {
  return alternatePrices.map((alternatePrice) => ({
    ...alternatePrice,
    lifecycleStageIds: toArray(alternatePrice.lifecycleStageIds),
    locationId: alternatePrice.locationId
      ? String(alternatePrice.locationId)
      : '',
    processorPlan: alternatePrice.processorPlan || '',
    requiredPackageIds: toArray(alternatePrice.requiredPackageIds),
  }));
};

export const normalizeAlternatePricesForRequest = (
  alternatePrices: any[] = []
) => {
  return alternatePrices.map((alternatePrice) => {
    const normalizedAlternatePrice = {
      ...alternatePrice,
      required_package_ids: toArray(alternatePrice.required_package_ids),
    };

    delete normalizedAlternatePrice.created_at;
    delete normalizedAlternatePrice.updated_at;

    return normalizedAlternatePrice;
  });
};

const parsePackageDate = (value: Date | string | number): Date => {
  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'number') {
    return timestampToDate(value);
  }

  return parseISO(value);
};

// Use TypeScript's type annotation to enforce the type of the input parameter
// and the return type of the function.
export function normalizePackage(pkg: any): Package {
  const camelized = camelcaseKeys(pkg, { deep: true }) as Package;
  camelized.fbmCategoryIds ??= [];

  if (camelized.presetActivationDate) {
    camelized.presetActivationDate = parsePackageDate(
      camelized.presetActivationDate
    );
  }

  if (camelized.presetExpirationDate) {
    camelized.presetExpirationDate = parsePackageDate(
      camelized.presetExpirationDate
    );
  }

  if (camelized.enrollmentBeginDate) {
    camelized.enrollmentBeginDate = parsePackageDate(
      camelized.enrollmentBeginDate
    );
  }

  if (camelized.enrollmentEndDate) {
    camelized.enrollmentEndDate = parsePackageDate(camelized.enrollmentEndDate);
  }

  camelized.serviceCounts?.forEach((serviceCount: ServiceCount) => {
    if (serviceCount.maxRollover) {
      serviceCount.rolloverVisits = parseInt(
        serviceCount.maxRollover.toString()
      );
    }

    if (!serviceCount.cadence) {
      const legacyMixed =
        serviceCount.count > 0 && hasIntervalLimit(serviceCount);

      if (legacyMixed) {
        serviceCount.cadence = null;
      } else {
        const isCustomInterval =
          !!serviceCount.visitsInterval && serviceCount.count === 0;
        if (isCustomInterval) {
          serviceCount.cadence = 'custom';
        } else if (serviceCount.count > 0) {
          serviceCount.cadence = 'hard_limit';
        } else {
          serviceCount.cadence = 'follow_plan';
        }
      }
    }

    serviceCount._unlimited = serviceCount.count === 0;

    const isCustomInterval =
      serviceCount.cadence === 'custom' ||
      (!!serviceCount.visitsInterval && serviceCount.count === 0);

    serviceCount._limitBooking =
      serviceCount.count > 0 ||
      (isCustomInterval && !!serviceCount.maxVisits) ||
      (serviceCount.cadence === 'follow_plan' && !!serviceCount.maxVisits);
  });

  if (camelized.alternatePrices) {
    camelized.alternatePrices = normalizeAlternatePricesForForm(
      camelized.alternatePrices
    );
  }

  if (camelized.requiredAssessment) {
    camelized.requiredAssessment = parseInt(
      camelized.requiredAssessment.toString()
    );
  }

  if (camelized.requiredSequence) {
    camelized.requiredSequence = parseInt(
      camelized.requiredSequence.toString()
    );
  }

  return camelized;
}

// Here, Partial<Package> is used to signify that the function can accept
// an object that may not have all the fields from Package.
// The return type is set to any as the shape of the object can vary after decamelization.
export function denormalizePackage(pkg: Partial<Package>): any {
  const pack = decamelizeKeys(pkg, { deep: true });

  if (pack.one_time_payment === '') {
    pack.one_time_payment = null;
  }

  pack.service_counts?.forEach((serviceCount: ServiceCount) => {
    removeReadOnlyFields(serviceCount, serviceCountReadOnlyFields);
  });

  if (pack.alternate_prices) {
    pack.alternate_prices = normalizeAlternatePricesForRequest(
      pack.alternate_prices
    );
  }

  removeReadOnlyFields(pack, readOnlyFields);

  if (pack.preset_activation_date instanceof Date) {
    pack.preset_activation_date = formatISODate(pack.preset_activation_date);
  }

  if (pack.preset_expiration_date instanceof Date) {
    pack.preset_expiration_date = formatISODate(pack.preset_expiration_date);
  }

  if (pack.enrollment_begin_date instanceof Date) {
    pack.enrollment_begin_date = formatISODate(pack.enrollment_begin_date);
  }

  if (pack.enrollment_end_date instanceof Date) {
    pack.enrollment_end_date = formatISODate(pack.enrollment_end_date);
  }

  return pack;
}

export function normalizePackageSearchResult(result: any): PackageSearchResult {
  const { json } = result.data;
  const camelized = camelcaseKeys(json);
  const searchResult: PackageSearchResult = {
    id: camelized.id,
    name: result.term,
    aliases: result.aliases,
  };

  return searchResult;
}

export interface BulkPackagesPayload {
  paymentMethod: string;
  clientIds: any[];
  packages: Array<{
    packageId: any;
    quantity?: number;
    paymentOptionId?: string;
    customPrice?: number; // Custom price in dollars (for purchases)
    customPlanId?: string; // Stripe plan ID (for subscriptions)
    activationDate?: Date;
    expiresOn?: Date;
    startOn?: Date;
    couponId?: any;
    chargedById?: any;
    primaryCommissioneeIds?: any[];
    secondaryCommissioneeIds?: any[];
    cancelDate?: Date;
    billingCycleAnchor?: Anchor | string | number | null;
    overrideProrationBehavior?: Anchor | string | null;
    backdateIsStartdate?: boolean;
    shareWithAllDependents?: boolean | number;
  }>;
}

/** Cart ItemSettings uses one startOn; ChargePackage expects future → start_on, past → backdate. */
function splitSubscriptionStartDate(startOn?: Date): {
  startOn?: string;
  backdate?: string;
} {
  if (!startOn) return {};

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(startOn);
  day.setHours(0, 0, 0, 0);

  if (day > today) return { startOn: formatISODate(startOn) };
  if (day < today) return { backdate: formatISODate(startOn) };
  return {};
}

function anchorValue(
  anchor: Anchor | string | number | null | undefined
): string | number | undefined {
  if (anchor == null) return undefined;
  if (typeof anchor === 'object') return anchor.value;
  return anchor;
}

export function denormalizeBulkPackages(payload: BulkPackagesPayload): any {
  // Map payment method to match v2 format
  const paymentMethodMap: Record<string, string> = {
    card_on_file: 'card',
    cash: 'cash',
    check: 'check',
    free: 'free',
    other: 'other',
  };

  const camelCasePayload = {
    paymentMethod:
      paymentMethodMap[payload.paymentMethod] || payload.paymentMethod,
    chargeUsers: payload.clientIds,
    chargeRequestId: Date.now(), // Timestamp for duplicate prevention
    existingPackages: payload.packages.map((pkg) => {
      const { startOn, backdate } = splitSubscriptionStartDate(pkg.startOn);
      const prorate = anchorValue(pkg.overrideProrationBehavior);
      const billingCycleAnchor = anchorValue(pkg.billingCycleAnchor);

      // Keys match PackageAssigner / ChargePackage (Ember charge-package existing_packages)
      const packageConfig = {
        id: pkg.packageId,
        quantity: pkg.quantity || 1,
        customPrice: pkg.customPrice,
        customPlanId: pkg.customPlanId,
        // YYYY-MM-DD — ChargePackage uses Date.strptime
        activationDate: pkg.activationDate
          ? formatISODate(pkg.activationDate)
          : undefined,
        expiresOn: pkg.expiresOn ? formatISODate(pkg.expiresOn) : undefined,
        startOn,
        backdate,
        backdateAnchorDate: pkg.backdateIsStartdate ? true : undefined,
        cancelOn: pkg.cancelDate ? formatISODate(pkg.cancelDate) : undefined,
        coupon: pkg.couponId || null,
        userIds: [],
        chargedById: pkg.chargedById,
        primaryCommissioneeIds: pkg.primaryCommissioneeIds || [],
        secondaryCommissioneeIds: pkg.secondaryCommissioneeIds || [],
        billingCycleAnchor,
        prorate,
        shareWithAllDependents: pkg.shareWithAllDependents ? true : undefined,
      };

      // Remove undefined/null values before converting to snake_case
      return Object.fromEntries(
        Object.entries(packageConfig).filter(
          ([_, value]) => value !== undefined && value !== null
        )
      );
    }),
  };

  // Convert to snake_case for API
  const apiPayload = decamelizeKeys(camelCasePayload, { deep: true });

  // Stringify existing_packages AFTER decamelizing because PackageAssigner expects a JSON string
  apiPayload.existing_packages = JSON.stringify(apiPayload.existing_packages);

  return apiPayload;
}
